import { Box, Button, SvgIcon, Tooltip, useTheme } from "@mui/material";

import { ReactComponent as GenieLogo } from "@/common/assets/vectors/genie_logo.svg";

import type { AskGenieButtonComponentRenderer } from "./AskGenieButtonView";

const isMacPlatform = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);

/**
 * The Genie button labelled as the way in, for an app's top bar: "Ask Genie"
 * with its shortcut. It toggles; ⌘K only ever opens (useOpenGenieShortcut).
 */
const GenieLauncherPill = ({
  isOpen,
  isDisabled,
  onClick,
  dataTestId,
}: {
  isOpen: boolean;
  isDisabled: boolean;
  onClick: () => void;
  dataTestId: string;
}) => {
  const theme = useTheme();
  const shortcut = isMacPlatform() ? "⌘K" : "Ctrl K";

  return (
    <Tooltip title={isOpen ? "Close Genie" : `Ask Genie (${shortcut})`}>
      <Button
        aria-pressed={isOpen}
        data-testid={dataTestId}
        onClick={onClick}
        disabled={isDisabled}
        variant={isOpen ? "outlined" : "contained"}
        color="secondary"
        startIcon={
          <SvgIcon
            component={GenieLogo}
            viewBox="0 0 24 24"
            sx={{ width: 20, height: 20 }}
          />
        }
        sx={{
          borderRadius: 99,
          px: 2,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: isOpen ? "none" : theme.shadows[2],
        }}
      >
        Ask Genie
        <Box
          component="kbd"
          sx={{
            ml: 1,
            px: 0.75,
            borderRadius: 1,
            fontFamily: "inherit",
            fontSize: 11,
            fontWeight: 500,
            opacity: 0.7,
            border: 1,
            borderColor: "currentColor",
            display: { xs: "none", sm: "inline-block" },
          }}
        >
          {shortcut}
        </Box>
      </Button>
    </Tooltip>
  );
};

/** `AskGenieButtonView`'s `variant` for a top bar: the labelled pill. */
export const genieLauncherPill =
  ({
    isOpen,
    onClick,
    dataTestId,
  }: {
    isOpen: boolean;
    onClick: () => void;
    dataTestId: string;
  }): AskGenieButtonComponentRenderer =>
  ({ isDisabled }) => (
    <GenieLauncherPill
      isOpen={isOpen}
      isDisabled={isDisabled}
      onClick={onClick}
      dataTestId={dataTestId}
    />
  );
